/** Bsky's PDS rejects any image blob > 1_000_000 bytes (the
 *  app.bsky.embed.images#image lex hard-caps at exactly 1 MB). The
 *  reference bsky.app client downscales + re-encodes on the way in;
 *  this is our equivalent.
 *
 *  We aim for 950 KB to leave headroom for JPEG container overhead +
 *  the multipart wrapping the PDS adds, and downscale the longest
 *  side to 2000 px (also the bsky.app default cap). If even quality
 *  0.5 at 2000 px is too large, fall back to 1600 px, then 1200 px. */

const TARGET_BYTES = 950_000
const MAX_DIM = 2000
const FALLBACK_DIMS = [1600, 1200, 900]

export interface CompressedResult {
  blob: Blob
  width: number
  height: number
  /** True when we actually re-encoded (vs. passing the original
   *  through because it was already small enough + JPEG/PNG). */
  recompressed: boolean
  /** Bytes of the original input — handy for "X → Y MB" UI hints. */
  originalSize: number
}

/** Decode an arbitrary image Blob into an HTMLImageElement. Works
 *  for jpeg / png / webp / gif on all modern browsers; on Safari it
 *  also handles HEIC. On Chrome / Firefox, HEIC will fail to decode
 *  — we surface that as a clear error so the caller can show it.
 *  HTMLImageElement is used rather than createImageBitmap because
 *  ImageBitmap doesn't decode HEIC on Chrome at all. */
function decodeImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(
        new Error(
          'could not decode image — HEIC / HEIF files from iPhones may need to be converted to JPEG first.',
        ),
      )
    }
    img.src = url
  })
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (!b) reject(new Error('canvas.toBlob returned null'))
        else resolve(b)
      },
      'image/jpeg',
      quality,
    )
  })
}

function fitInside(
  width: number,
  height: number,
  maxDim: number,
): { width: number; height: number } {
  if (width <= maxDim && height <= maxDim) return { width, height }
  if (width >= height) {
    return { width: maxDim, height: Math.round((height * maxDim) / width) }
  }
  return { width: Math.round((width * maxDim) / height), height: maxDim }
}

/**
 * Take an image Blob and return one guaranteed to fit under bsky's
 * 1 MB per-image limit. If the input is already a JPEG / PNG under
 * `targetBytes`, returns it unchanged (recompressed=false). Otherwise
 * re-encodes to JPEG with iterative quality reduction.
 */
export async function compressImageToFit(
  input: Blob,
  opts: { targetBytes?: number; maxDim?: number } = {},
): Promise<CompressedResult> {
  const targetBytes = opts.targetBytes ?? TARGET_BYTES
  const maxDim = opts.maxDim ?? MAX_DIM
  const originalSize = input.size
  // Fast path: already small + a format bsky accepts.
  if (
    input.size <= targetBytes &&
    (input.type === 'image/jpeg' || input.type === 'image/png')
  ) {
    const img = await decodeImage(input).catch(() => null)
    return {
      blob: input,
      width: img?.naturalWidth ?? 0,
      height: img?.naturalHeight ?? 0,
      recompressed: false,
      originalSize,
    }
  }
  const img = await decodeImage(input)
  const dimsToTry = [maxDim, ...FALLBACK_DIMS]
  for (const dim of dimsToTry) {
    const { width, height } = fitInside(img.naturalWidth, img.naturalHeight, dim)
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('canvas 2d context unavailable')
    // Black background so JPEG re-encoding of transparent PNGs
    // doesn't show as random green/black noise on bsky.
    ctx.fillStyle = '#000'
    ctx.fillRect(0, 0, width, height)
    ctx.drawImage(img, 0, 0, width, height)
    for (const q of [0.85, 0.75, 0.65, 0.55, 0.45]) {
      const out = await canvasToBlob(canvas, q)
      if (out.size <= targetBytes) {
        return {
          blob: out,
          width,
          height,
          recompressed: true,
          originalSize,
        }
      }
    }
  }
  throw new Error(
    `could not compress image under ${(targetBytes / 1024).toFixed(0)} KB — try a smaller source image`,
  )
}
